import React from 'react';
import { Table, Button, Space, Tag, Tooltip, Modal } from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    EyeOutlined,
    DeleteOutlined,
    KeyOutlined,
    UserOutlined
} from '@ant-design/icons';
import { NubUserDTO } from '../types';

interface NubUserListProps {
    users: NubUserDTO[];
    onUserAction: (user: NubUserDTO | null, action: 'view' | 'add' | 'edit' | 'delete') => void;
    onResetPassword?: (userId: string) => void;
}

export const NubUserList: React.FC<NubUserListProps> = ({
                                                            users,
                                                            onUserAction,
                                                            onResetPassword
                                                        }) => {
    const columns = [
        {
            title: 'User ID',
            dataIndex: 'userId',
            key: 'userId',
            width: '15%',
        },
        {
            title: 'Full Name',
            dataIndex: 'fullName',
            key: 'fullName',
            width: '20%',
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            width: '20%',
        },
        {
            title: 'Primary Group',
            dataIndex: ['primaryGroup', 'name'],
            key: 'primaryGroup',
            width: '15%',
            render: (text: string, record: NubUserDTO) => (
                <Tag color={record.primaryGroup?.primary ? 'blue' : 'default'}>
                    {text}
                </Tag>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'enabled',
            key: 'enabled',
            width: '10%',
            render: (enabled: boolean) => (
                <Tag color={enabled ? 'success' : 'error'}>
                    {enabled ? 'Active' : 'Inactive'}
                </Tag>
            ),
        },
        {
            title: 'Type',
            dataIndex: 'userType',
            key: 'userType',
            width: '10%',
            render: (type: string) => (
                <Tag color={
                    type === 'STAFF' ? 'purple' :
                        type === 'MEMBER' ? 'green' :
                            'orange'
                }>
                    {type}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: '15%',
            render: (_: any, record: NubUserDTO) => (
                <Space size="small">
                    <Tooltip title="View">
                        <Button
                            type="text"
                            icon={<EyeOutlined />}
                            onClick={() => onUserAction(record, 'view')}
                        />
                    </Tooltip>
                    <Tooltip title="Edit">
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => onUserAction(record, 'edit')}
                        />
                    </Tooltip>
                    <Tooltip title="Reset Password">
                        <Button
                            type="text"
                            icon={<KeyOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                Modal.confirm({
                                    title: 'Reset Password',
                                    content: `Are you sure you want to reset password for ${record.fullName}?`,
                                    okText: 'Yes',
                                    cancelText: 'No',
                                    // @ts-ignore
                                    onOk: () => onResetPassword?.(record.userId)
                                });
                            }}
                        />
                    </Tooltip>
                    <Tooltip title="Delete">
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                Modal.confirm({
                                    title: 'Delete User',
                                    content: `Are you sure you want to delete user ${record.fullName}?`,
                                    okText: 'Yes',
                                    okType: 'danger',
                                    cancelText: 'No',
                                    onOk: () => onUserAction(record, 'delete')
                                });
                            }}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div className="mb-4 flex justify-end">
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => onUserAction(null, 'add')}
                >
                    Add User
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={users}
                rowKey="userId"
                expandable={{
                    expandedRowRender: (record) => (
                        <div className="p-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <h4 className="font-semibold mb-2">Additional Groups</h4>
                                    <Space wrap>
                                        {record.groups?.map((group) => (
                                            <Tag key={group.code}>
                                                {group.name}
                                            </Tag>
                                        ))}
                                    </Space>
                                </div>
                                <div>
                                    <h4 className="font-semibold mb-2">Contact Information</h4>
                                    <p><strong>Phone:</strong> {record.phoneNo}</p>
                                    {record.userType === 'STAFF' && (
                                        <p><strong>FS User ID:</strong> {record.fsUserId || '-'}</p>
                                    )}
                                    {record.userType === 'MEMBER' && (
                                        <>
                                            <p><strong>Customer No:</strong> {record.custNo}</p>
                                            <p><strong>Branch:</strong> {record.branch}</p>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    ),
                }}
            />
        </div>
    );
};